import { useEffect, useRef, useState, useCallback } from "react";
import { debounce } from "../../../other/utils/otherUtils.jsx";
import { EntityRecord } from "@/types/entity";

interface IUseSwivuTaskLocationProps {
  record: EntityRecord;
  recordField?: string;
}

export interface IFormattedLocation {
  formattedAddress: string;
  lat: number | null;
  lon: number | null;
  isResolved: boolean;
}

export interface IUseSwivuTaskLocationReturn {
  searchResults: { label: string, value: string }[];
  onSearchAddress: (searchValue: string) => void;
  setResolvedLocation: (googleLocationId: string) => void;
  setUnresolvedLocation: (addressValue: string) => void;
  formattedLocation: IFormattedLocation;
  isLoading: boolean;
  clearSearchResults: () => void;
}

const request = {
  language: "en-US",
  includedRegionCodes: ["US"]
};

export const useSwivuTaskLocation = ({ record, recordField = "address" }: IUseSwivuTaskLocationProps): IUseSwivuTaskLocationReturn => {
  const isInit = useRef(false);
  const googlePlacesSession = useRef(null);
  const googlePlaceSuggestions = useRef(new Map());
  const [searchResults, setSearchResults] = useState<{ label: string, value: string }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const clearSearchResults = () => {
    setSearchResults([]);
    googlePlaceSuggestions.current = new Map();
  };

  const selectedLocation = record[recordField] || {};
  const formattedLocation: IFormattedLocation = {
    formattedAddress: selectedLocation.formatted_address || "",
    lat: selectedLocation.lat || null,
    lon: selectedLocation.lon || null,
    isResolved: selectedLocation.lat !== null && selectedLocation.lon !== null
  };

  useEffect(() => {
    // @ts-expect-error Google Maps typings missing
    if (window.googleMapsLoaded && !isInit.current) {
      init();
    } else {
      window.addEventListener("googleMapsLoaded", () => {
        init();
      });
    }
  }, []);

  const init = () => {
    isInit.current = true;
  }

  const startSession = () => {
    if (!googlePlacesSession.current) {
      // @ts-expect-error Google Maps typings missing
      googlePlacesSession.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const endSession = () => {
    googlePlacesSession.current = null;
  };

  const fetchAddress = async (value: string) => {
    if (!isInit.current || !value.trim()) return;

    startSession();
    setIsLoading(true);

    // @ts-expect-error Google Maps typings missing
    request.input = value;
    // @ts-expect-error Google Maps typings missing
    request.sessionToken = googlePlacesSession.current;

    try {
      // @ts-expect-error Google Maps typings missing
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedSuggestions = suggestions.map((placeInstance: any) => {
        if (!placeInstance.placePrediction) {
          return { label: '', value: '' };
        }
        googlePlaceSuggestions.current.set(placeInstance.placePrediction.placeId, placeInstance);
        const placeName = placeInstance.placePrediction.text.toString();
        return {
          label: placeName,
          value: placeInstance.placePrediction.placeId
        };
      })

      setSearchResults(formattedSuggestions);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      clearSearchResults();
    }
  };

  const debouncedFetchAddress = useCallback(debounce(async (searchValue: string) => {
    try {
      await fetchAddress(searchValue);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      clearSearchResults();
    } finally {
      setIsLoading(false);
    }
  }, 300), []);

  const onSearchAddress = (searchValue: string) => {
    setIsLoading(true);
    clearSearchResults();
    if (!searchValue.trim()) {
      setIsLoading(false);
      return;
    }

    debouncedFetchAddress(searchValue);
  };

  const setResolvedLocation = useCallback(debounce(async (googleLocationId: string) => {
    try {
      setIsLoading(true);

      const googlePlaceInstance = googlePlaceSuggestions.current.get(googleLocationId);
      if (!googlePlaceInstance) {
        throw new Error("Google place instance not found");
      }

      const place = googlePlaceInstance.placePrediction.toPlace();

      // Fetch the location details including coordinates
      await place.fetchFields({ fields: ["location"] });

      const lat = place.location.lat();
      const lon = place.location.lng();
      const formattedAddress = googlePlaceInstance.placePrediction.text.toString();

      // Update the record
      record[recordField] = {
        formatted_address: formattedAddress,
        lat,
        lon
      };

      clearSearchResults();

      // End the session after successful selection
      endSession();
    } catch (error) {
      console.error("Error selecting location:", error);
    } finally {
      setIsLoading(false);
    }
  }, 300), [record, recordField]);

  const setUnresolvedLocation = useCallback(debounce((addressValue: string) => {
    clearSearchResults();
    record[recordField] = {
      formatted_address: addressValue,
      lat: null,
      lon: null
    };
  }, 300), [record, recordField]);

  return {
    searchResults,
    onSearchAddress,
    setResolvedLocation,
    setUnresolvedLocation,
    formattedLocation,
    isLoading,
    clearSearchResults
  };
};
